import React, { FC, useEffect, useRef, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Html } from '../html/Html';
import { ArrowDown } from '../icon';
import { Link } from '../link/Link';
import { Surface, SurfaceProps } from '../surface/Surface';
import { Stack } from '../stack/Stack';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import tokens from './tokens';

export interface ExpanderContentProps {
  expanderTitle?: string;
  expanderOpenTitle?: string;
  expanderContent?: string | React.ReactNode;
  backgroundColor?: ThemeColorsKeys;
  collapse?: SurfaceProps['collapse'];
  collapseVertical?: SurfaceProps['collapseVertical'];
  defaultOpen?: boolean;
  defaultOpenDesktop?: boolean;
  defaultOpenMobile?: boolean;
  excludeHtmlStyles?: boolean;
}

const StyledExpander = styled(Stack)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.large};
  `};
`;

export const ExpanderContent: FC<ExpanderContentProps> = ({
  expanderTitle,
  expanderOpenTitle,
  expanderContent,
  backgroundColor,
  collapse,
  collapseVertical,
  defaultOpen = false,
  defaultOpenDesktop,
  defaultOpenMobile,
  excludeHtmlStyles = false,
}) => {
  const { dataLayerPush } = useDataLayerPush();
  const { media } = useTheme();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const hasInteracted = useRef(false);
  const hasAppliedResponsiveDefault = useRef(false);
  const hasResponsiveDefault =
    defaultOpenDesktop !== undefined || defaultOpenMobile !== undefined;

  useEffect(() => {
    if (!hasResponsiveDefault || hasAppliedResponsiveDefault.current) {
      return;
    }

    hasAppliedResponsiveDefault.current = true;

    if (hasInteracted.current) {
      return;
    }

    const isDesktop = window.matchMedia(`(min-width: ${media.medium})`).matches;

    setIsOpen(
      isDesktop
        ? (defaultOpenDesktop ?? defaultOpen)
        : (defaultOpenMobile ?? defaultOpen),
    );
  }, [
    defaultOpen,
    defaultOpenDesktop,
    defaultOpenMobile,
    hasResponsiveDefault,
    media.medium,
  ]);

  const handleClick = () => {
    hasInteracted.current = true;

    // Toggle the isOpen state
    const newIsOpen = !isOpen;
    setIsOpen(!isOpen);

    const expanderText = newIsOpen
      ? expanderTitle
      : expanderOpenTitle || expanderTitle;

    // Execute dataLayerPush
    dataLayerPush({
      event: 'click_expander',
      expander_class: newIsOpen ? 'expander opened' : 'expander closed', // Set the class based on the new state
      expander_text: expanderText,
    });
  };

  const linkLabel =
    isOpen && expanderOpenTitle ? expanderOpenTitle : expanderTitle;

  return (
    <StyledExpander gap="small">
      <Link
        onClick={handleClick}
        label={linkLabel}
        icon={<ArrowDown size="small" />}
        darkIcon={tokens.darkIcon}
        aria-expanded={isOpen}
        circle
        rotateIcon={isOpen}
        type="button"
        data-test="expander-link"
      />
      {isOpen && (
        <Surface
          backgroundColor={backgroundColor || 'utilityInformationBackground'}
          aria-hidden={!isOpen}
          collapse={collapse}
          collapseVertical={collapseVertical}
          padding="fluidMedium"
        >
          {excludeHtmlStyles ? expanderContent : <Html>{expanderContent}</Html>}
        </Surface>
      )}
    </StyledExpander>
  );
};

export default ExpanderContent;
